import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { media } from '@breakpoints'
import Link from '@atoms/link'
import SearchBar from '@organisms/search-bar'
import Text from '@particles/text'
import Icon from '@icons'
import Layout from '@organisms/layout'
import { Defaults } from '@styles/theme'
import Button from '@atoms/button'
import { NavLink } from '@src/atoms/menu/links'
import logoBlack from '@assets/logo-text-black.svg'

const propTypes = {}

export const HeaderContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: '3rem';
  position: sticky;
  top: 0;
  z-index: 100;
  background: #2a2f3c;
`

const Dropdown = styled.ul`
  background: white;
  position: absolute;
  top: 3rem;
  left: 0;
  box-shadow: 0 15px 20px -10px rgb(32 51 70 / 30%),
    0 0px 30px 0px rgb(32 51 70 / 20%);
  transition: 0.3s opacity 0.06s ease, 0.3s transform 0.06s ease,
    0.3s visibility 0.06s ease;
  border: 1px solid #ddd;
  border-radius: ${Defaults.borderRadius};
  padding: 0.25rem 1.25rem;
  visibility: hidden;
  opacity: 0;
  list-style: none;
  width: 20rem;

  ${
    /* sc-selector */ media.tablet`
    position: fixed;
    left: 0;
    width: 100%;
    top: 4rem;
    transition: none;
  `
  }

  ::before {
    position: absolute;
    transform: rotateZ(45deg);
    top: -7.5px;
    left: 2rem;
    content: '';
    width: 15px;
    height: 15px;
    border-radius: 3px;
    background-color: white;
    border-left: 1px solid #ddd;
    border-top: 1px solid #ddd;
    ${media.tablet`
      right: 1.5rem;
      left: auto;
    `}
  }

  ::after {
    ${media.tablet`
      width: 0;
      height: 0;
    `}
    position: absolute;
    content: '';
    width: 7rem;
    height: 4rem;
    top: -3rem;
    left: 0rem;
  }
`

const StyledButton = styled(Button)`
  background: #75e1b2;
  color: #014c33;
  border-radius: 2rem;
  padding: 0.5rem 1rem;
  font-size: 1rem;
`

const Nav = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  padding: 0 1rem;
  margin: auto;
  display: flex;
  align-items: center;
  position: relative;
  z-index: 100;
  ul {
    margin: 0;
    padding: 0;
    list-style: none;
    height: 100%;
  }
  position: sticky;
  top: 0;
  background: ${({ home }) => (home ? `#181C26` : `rgb(42, 47, 60)`)};
`

const MobileNav = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  padding: 0 1rem;
  border-radius: 2rem;
  align-items: center;

  ${media.desktop_small_up`
    display: none;
  `}
`

const DesktopNav = styled.div`
  max-width: 1280px;
  display: grid;
  grid-template-columns: ${({ home }) => (home ? '1fr 1fr' : '1fr 2fr 1fr')};
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 0 1rem;
  border-radius: 2rem;

  ${media.desktop_small`
    display: none;
  `}
`

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const CtaContainer = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: right;
  justify-self: flex-end;
  width: max-content;
  gap: 1rem;
  ${NavLink} {
    white-space: nowrap;
  }
`

const MenuLink = styled(Link)`
  color: white;
  transition: 0.2s opacity;

  &:hover {
    color: white;
    opacity: 0.8;
  }
`

const ProductListComponent = ({ className, icon, name, desc, href }) => {
  return (
    <li className={className}>
      <a href={href}>
        <div className="product-icon">
          <Icon name={icon} />
        </div>
        <Layout.Column margin="L1">
          <Text as="span" bold color="300" spacing="0.06">
            {name}
          </Text>
          <Text as="div" size={0.75} spacing="0.06">
            {desc}
          </Text>
        </Layout.Column>
      </a>
    </li>
  )
}

ProductListComponent.propTypes = {
  icon: PropTypes.string,
  name: PropTypes.string,
  desc: PropTypes.string,
  href: PropTypes.string,
  className: PropTypes.string
}

const ProductList = styled(ProductListComponent)`
  .product-icon {
    ${Icon} {
      width: 35px;
      height: 35px;
    }

    margin: 1rem 0;
    border-radius: 100%;
    min-width: 55px;
    min-height: 55px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${(props) => props.iconBackground};
  }

  a {
    padding: 1rem 0.5rem;
    margin: 1rem 0;
    display: flex;
    align-items: center;
    height: 70px;
    text-decoration: none;
  }

  transition: background-color 0.3s ease;
  :hover {
    background-color: ${(props) => props.iconBackground};
  }
`

const HeaderDropdownComponent = ({ className, isMobile = false }) => {
  return (
    <Layout.Row center className={className} margin="L2">
      {isMobile ? (
        <Icon name="grid" size={2} />
      ) : (
        <>
          <Text margin="R0.5" as="span" bold color="300">
            Products
          </Text>
          <Icon name="chevron" size={0.75} />
        </>
      )}
      <Dropdown>
        <ProductList
          icon="klips"
          iconBackground="#e0f8ff"
          name="Klips"
          desc="Highly customizable reporting automation tool"
          href="https://klipfolio.com"
        />
        <ProductList
          icon="powermetrics"
          iconBackground="#edf3fd"
          name="PowerMetrics"
          desc={"Track and analyze your team's metrics all in one place"}
          href="https://www.powermetrics.app"
        />
        <ProductList
          icon="metrichq"
          iconBackground="#e8fde2"
          name="MetricHQ"
          desc="The best source to learn about metrics and KPIs"
          href="https://klipfolio.com/metrichq"
        />
      </Dropdown>
    </Layout.Row>
  )
}

const LogoContainer = styled.div`
  @media (max-width: 500px) {
    width: 28px;
    overflow: hidden;
  }
`

HeaderDropdownComponent.propTypes = {
  isMobile: PropTypes.bool,
  className: PropTypes.string
}

const HeaderComponent = ({ location }) => {
  const home = useMemo(
    () => location?.pathname === '/' || location?.pathname === '/chatbot/',
    [location]
  )
  return (
    <Nav as={'nav'} home={home}>
      <MobileNav>
        <Link to="/">
          <LogoContainer>
            <img
              src={logoBlack}
              width="150"
              alt="MetricHQ Logo"
              style={{ marginTop: '3px' }}
            />
          </LogoContainer>
        </Link>
        <div style={{ flex: 1 }}></div>
        <CtaContainer>
          <MenuLink href="/all-metrics/" title={'Metrics'}>
            Metrics
          </MenuLink>
          <MenuLink href="/all-services/" title={'Services'}>
            Services
          </MenuLink>
          <SearchBar placeholder={'Search for a metric'} nav mobile />
        </CtaContainer>
      </MobileNav>
      <DesktopNav home={home}>
        <Link to="/">
          <img
            src={logoBlack}
            width="150"
            alt="MetricHQ Logo"
            style={{ marginTop: '3px' }}
          />
        </Link>
        {!home && (
          <SearchWrapper>
            <SearchBar placeholder={'Search for a metric'} nav />
          </SearchWrapper>
        )}
        <CtaContainer>
          {!home && (
            <>
              <MenuLink href="/all-metrics/" title={'Metrics'}>
                Metrics
              </MenuLink>
              <MenuLink href="/all-services/" title={'Services'}>
                Services
              </MenuLink>
            </>
          )}
          <StyledButton href="/contribute/">Contribute</StyledButton>
        </CtaContainer>
      </DesktopNav>
    </Nav>
  )
}

HeaderComponent.propTypes = {
  location: PropTypes.any,
  className: PropTypes.string,
  maxMedia: PropTypes.string,
  children: PropTypes.array
}

const Header = styled(HeaderComponent)`
  display: grid;
  grid-template-columns: 156px 1fr 260px;
  max-width: ${({ theme }) => theme.default.container}px;
  ${(props) =>
    props.maxMedia === 'desktop_medium' &&
    media.desktop_medium_up`
    max-width: 1280px;
    display: grid;
    grid-template-columns: 156px 1fr 260px;
    grid-gap: 2rem;
    button {
      margin-left: 0px;
    }
  `}
  height: 61px;
  align-items: center;
  width: 100%;
  padding: 0 1rem;

  ${Link} {
    & > img {
      height: 50px;
    }
  }

  ${SearchBar} {
    margin-left: 2rem;
  }
`

Header.propTypes = propTypes
export default Header
